import React, { useState } from "react";
import { Field, Form, Formik, FormikProps } from "formik";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { notificationApi } from "../../../../services/notificationApi";
import { NotificationComposeInput } from "../../../../types/app/notificationTypes";
import { ConfirmationModal } from "../../../../shared/components/modals";
import NotificationPreview from "./NotificationPreview";
import {
  buildNotificationCommand,
  initialNotificationValues,
  splitNotificationEmails,
  selectedNotificationCity,
  validateNotificationCompose,
} from "./notificationComposerModel";
import { notificationCitiesByProvince, notificationProvinceOptions, OTHER_CITY } from "./notificationLocations";
import styles from "./notifications.module.scss";

const canSendNotification = (values: NotificationComposeInput): boolean =>
  (values.recipientTarget === "vendors" || values.recipientTarget === "users" || values.recipientTarget === "location" ||
    (values.recipientTarget === "emails" && splitNotificationEmails(values.emails).length === 1)) &&
  Object.keys(validateNotificationCompose(values)).length === 0;

const targetSummary = (values: NotificationComposeInput): string => {
  if (values.recipientTarget === "vendors") return "all vendors";
  if (values.recipientTarget === "users") return "all users";
  if (values.recipientTarget === "emails") return splitNotificationEmails(values.emails)[0];
  if (values.locationMode === "city") return `devices matching ${selectedNotificationCity(values)}, ${values.province.trim()}`;
  return `devices within ${String(values.locationRadiusKm ?? "").trim()} km of the specified coordinates`;
};

const NotificationComposer: React.FC = () => {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState("");
  const [pendingSend, setPendingSend] = useState<NotificationComposeInput | null>(null);
  const [isSending, setIsSending] = useState(false);

  const confirmSend = async () => {
    if (!pendingSend || isSending || !canSendNotification(pendingSend)) return;
    setIsSending(true);
    setSubmitError("");
    try {
      const result = await notificationApi.create(buildNotificationCommand(pendingSend));
      if (!result.success) throw new Error(result.message || "The notification was not accepted.");
      setPendingSend(null);
      navigate(result.id ? `/app/settings/notifications/${result.id}` : "/app/settings/notifications");
    } catch (error) {
      setPendingSend(null);
      setSubmitError(error instanceof Error ? error.message : "Could not send the notification. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={styles["notification-composer"]}>
      <button type="button" className={styles["notification-back-button"]} onClick={() => navigate("/app/settings/notifications")}>
        <FiArrowLeft aria-hidden="true" />
        <span>New notifications</span>
      </button>
      <p className={styles["notification-page-description"]}>Compose and send to a supported recipient target.</p>

      <Formik<NotificationComposeInput>
        initialValues={initialNotificationValues}
        validate={validateNotificationCompose}
        onSubmit={(values, helpers) => {
          setSubmitError("");
          if (canSendNotification(values) && !isSending) setPendingSend({ ...values });
          helpers.setSubmitting(false);
        }}
      >
        {(formik: FormikProps<NotificationComposeInput>) => (
          <Form>
            <div className={styles["notification-composer__layout"]}>
              <div className={styles["notification-composer__fields"]}>
                <div className={styles["notification-field"]}>
                  <label htmlFor="notification-target">Recipient target</label>
                  <Field as="select" id="notification-target" name="recipientTarget" aria-describedby="notification-target-help">
                    <option value="">Choose a target</option>
                    <option value="vendors">Vendors</option>
                    <option value="users">Users</option>
                    <option value="emails">Specific emails</option>
                    <option value="location">Location</option>
                  </Field>
                  <span id="notification-target-help" className={styles["notification-field__hint"]}>These targets are mutually exclusive in the current API.</span>
                  {formik.touched.recipientTarget && formik.errors.recipientTarget && <span className={styles["notification-field__error"]}>{formik.errors.recipientTarget}</span>}
                </div>

                {formik.values.recipientTarget === "emails" && (
                  <div className={styles["notification-field"]}>
                    <label htmlFor="notification-emails">Email addresses</label>
                    <Field as="textarea" id="notification-emails" name="emails" placeholder="name@example.com, another@example.com" aria-describedby="notification-emails-help" />
                    <span id="notification-emails-help" className={styles["notification-field__hint"]}>Separate addresses with commas, spaces, or new lines.</span>
                    {formik.touched.emails && formik.errors.emails && <span className={styles["notification-field__error"]}>{formik.errors.emails}</span>}
                  </div>
                )}

                {formik.values.recipientTarget === "location" && (
                  <>
                    <div className={styles["notification-field"]}>
                      <label htmlFor="notification-location-mode">Location method</label>
                      <Field as="select" id="notification-location-mode" name="locationMode">
                        <option value="city">City and province</option>
                        <option value="radius">Latitude, longitude and radius</option>
                      </Field>
                    </div>
                    {formik.values.locationMode === "city" ? (
                      <div className={styles["notification-field__pair"]}>
                        <div className={styles["notification-field"]}>
                          <label htmlFor="notification-province">Province</label>
                          <select
                            id="notification-province"
                            name="province"
                            value={formik.values.province}
                            onBlur={formik.handleBlur}
                            onChange={(event) => {
                              formik.setFieldValue("province", event.target.value);
                              formik.setFieldValue("city", "");
                              formik.setFieldValue("customCity", "");
                            }}
                          >
                            <option value="">Choose province</option>
                            {notificationProvinceOptions.map((province) => <option key={province.value} value={province.value}>{province.label}</option>)}
                          </select>
                          {formik.touched.province && formik.errors.province && <span className={styles["notification-field__error"]}>{formik.errors.province}</span>}
                        </div>
                        <div className={styles["notification-field"]}>
                          <label htmlFor="notification-city">City</label>
                          <Field as="select" id="notification-city" name="city" disabled={!formik.values.province}>
                            <option value="">Choose city</option>
                            {(notificationCitiesByProvince[formik.values.province] ?? []).map((city) => <option key={city} value={city}>{city}</option>)}
                            <option value={OTHER_CITY}>Other city…</option>
                          </Field>
                          {formik.touched.city && formik.errors.city && <span className={styles["notification-field__error"]}>{formik.errors.city}</span>}
                        </div>
                        {formik.values.city === OTHER_CITY && (
                          <div className={`${styles["notification-field"]} ${styles["notification-field__pair-full"]}`}>
                            <label htmlFor="notification-custom-city">Other city</label>
                            <Field id="notification-custom-city" name="customCity" type="text" placeholder="Enter city name" />
                            {formik.touched.customCity && formik.errors.customCity && <span className={styles["notification-field__error"]}>{formik.errors.customCity}</span>}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={styles["notification-field__triple"]}>
                        <div className={styles["notification-field"]}>
                          <label htmlFor="notification-latitude">Latitude</label>
                          <Field id="notification-latitude" name="locationLatitude" type="number" step="any" placeholder="43.6532" />
                          {formik.touched.locationLatitude && formik.errors.locationLatitude && <span className={styles["notification-field__error"]}>{formik.errors.locationLatitude}</span>}
                        </div>
                        <div className={styles["notification-field"]}>
                          <label htmlFor="notification-longitude">Longitude</label>
                          <Field id="notification-longitude" name="locationLongitude" type="number" step="any" placeholder="-79.3832" />
                          {formik.touched.locationLongitude && formik.errors.locationLongitude && <span className={styles["notification-field__error"]}>{formik.errors.locationLongitude}</span>}
                        </div>
                        <div className={styles["notification-field"]}>
                          <label htmlFor="notification-radius">Radius (km)</label>
                          <Field id="notification-radius" name="locationRadiusKm" type="number" min="0" step="any" placeholder="50" />
                          {formik.touched.locationRadiusKm && formik.errors.locationRadiusKm && <span className={styles["notification-field__error"]}>{formik.errors.locationRadiusKm}</span>}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className={styles["notification-field"]}>
                  <label htmlFor="notification-title">Title</label>
                  <Field id="notification-title" name="title" type="text" maxLength={80} placeholder="Enter a notification title" />
                  <div className={styles["notification-field__meta"]}>
                    {formik.touched.title && formik.errors.title ? <span className={styles["notification-field__error"]}>{formik.errors.title}</span> : <span />}
                    <span>{formik.values.title.length}/80</span>
                  </div>
                </div>

                <div className={styles["notification-field"]}>
                  <label htmlFor="notification-body">Body</label>
                  <Field as="textarea" id="notification-body" name="body" maxLength={240} placeholder="Enter the notification message" />
                  <div className={styles["notification-field__meta"]}>
                    {formik.touched.body && formik.errors.body ? <span className={styles["notification-field__error"]}>{formik.errors.body}</span> : <span />}
                    <span>{formik.values.body.length}/240</span>
                  </div>
                </div>

                <div className={styles["notification-composer__actions"]}>
                  <button type="button" className={styles["notification-secondary-button"]} disabled title="Draft creation is not supported by the API">
                    Save as draft
                  </button>
                  <button type="submit" className={styles["notification-primary-button"]} disabled={!canSendNotification(formik.values) || formik.isSubmitting || isSending || !!pendingSend} title={!canSendNotification(formik.values) ? "Complete a supported target, title, and body to send" : undefined}>
                    {isSending ? "Sending…" : "Send now"}
                  </button>
                </div>
                {submitError && <p className={styles["notification-composer__error"]} role="alert">{submitError}</p>}
                <p className={styles["notification-composer__prototype-note"]}>Sends support vendors, users, one specific email, or a location. Confirm the target carefully before sending.</p>
              </div>

              <NotificationPreview title={formik.values.title} body={formik.values.body} />
            </div>
          </Form>
        )}
      </Formik>
      <ConfirmationModal
        show={!!pendingSend}
        onHide={() => { if (!isSending) setPendingSend(null); }}
        onConfirm={confirmSend}
        title="Send notification?"
        message={pendingSend ? `Send “${pendingSend.title.trim()}” to ${targetSummary(pendingSend)}? This may reach multiple devices and cannot be undone.` : ""}
        confirmText={isSending ? "Sending…" : "Send now"}
        cancelText="Cancel"
        variant="primary"
        className={styles["notification-send-modal"]}
        isProcessing={isSending}
        closeOnConfirm={false}
      />
    </div>
  );
};

export default NotificationComposer;
